import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Partner {
  name: string;
  logo: string;
  alt: string;
  status: 'active' | 'integration' | 'coming-soon';
  region: string;
  integrationDate: string;
}

export default function AirlinePartners() {
  const partners: Partner[] = [
  {
    name: "British Airways",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_147391ffd-1772297225147.png",
    alt: "British Airways logo with iconic red, white and blue livery on aircraft tail",
    status: "active",
    region: "Europe",
    integrationDate: "2024-01"
  },
  {
    name: "Emirates",
    logo: "https://images.unsplash.com/photo-1712040935628-2394ca09f771",
    alt: "Emirates airline aircraft with distinctive red and gold branding",
    status: "active",
    region: "Middle East",
    integrationDate: "2024-03"
  },
  {
    name: "Singapore Airlines",
    logo: "https://images.unsplash.com/photo-1725087791673-279af1288c72",
    alt: "Singapore Airlines plane with signature blue and gold color scheme",
    status: "active",
    region: "Asia Pacific",
    integrationDate: "2024-02"
  },
  {
    name: "Lufthansa",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_12b856648-1772297225494.png",
    alt: "Lufthansa aircraft featuring iconic yellow and blue crane logo",
    status: "active",
    region: "Europe",
    integrationDate: "2023-11"
  },
  {
    name: "Qatar Airways",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1990b6792-1764768490697.png",
    alt: "Qatar Airways plane with burgundy livery and oryx logo",
    status: "active",
    region: "Middle East",
    integrationDate: "2024-04"
  },
  {
    name: "Air France",
    logo: "https://images.unsplash.com/photo-1575744380326-edb4c845f485",
    alt: "Air France aircraft with blue, white and red French flag colors",
    status: "integration",
    region: "Europe",
    integrationDate: "2026-02"
  },
  {
    name: "Cathay Pacific",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_15de3c15b-1772297228926.png",
    alt: "Cathay Pacific plane with distinctive green and white brushwing logo",
    status: "active",
    region: "Asia Pacific",
    integrationDate: "2024-05"
  },
  {
    name: "Turkish Airlines",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1467703a6-1772126508390.png",
    alt: "Turkish Airlines aircraft with red tulip logo on white fuselage",
    status: "integration",
    region: "Europe",
    integrationDate: "2026-03"
  },
  {
    name: "ANA",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_14246aeae-1764871462868.png",
    alt: "All Nippon Airways plane with blue and white livery",
    status: "active",
    region: "Asia Pacific",
    integrationDate: "2024-06"
  },
  {
    name: "KLM",
    logo: "https://images.unsplash.com/photo-1587067457393-668ec81677bf",
    alt: "KLM Royal Dutch Airlines aircraft with signature blue color",
    status: "coming-soon",
    region: "Europe",
    integrationDate: "2026-04"
  },
  {
    name: "Etihad Airways",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1ffbb4606-1772297230023.png",
    alt: "Etihad Airways plane with gold and brown desert-inspired livery",
    status: "active",
    region: "Middle East",
    integrationDate: "2024-07"
  },
  {
    name: "Swiss International",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1f5ac9310-1767034184472.png",
    alt: "Swiss International Air Lines aircraft with red and white Swiss cross",
    status: "coming-soon",
    region: "Europe",
    integrationDate: "2026-05"
  }];


  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center space-x-1 px-2 py-1 bg-success/10 text-success rounded-full text-xs font-medium">
            <Icon name="CheckCircleIcon" size={14} />
            <span>Live</span>
          </span>);

      case 'integration':
        return (
          <span className="inline-flex items-center space-x-1 px-2 py-1 bg-warning/10 text-warning rounded-full text-xs font-medium">
            <Icon name="ClockIcon" size={14} />
            <span>Integrating</span>
          </span>);

      case 'coming-soon':
        return (
          <span className="inline-flex items-center space-x-1 px-2 py-1 bg-muted text-muted-foreground rounded-full text-xs font-medium">
            <Icon name="SparklesIcon" size={14} />
            <span>Coming Soon</span>
          </span>);

      default:
        return null;
    }
  };

  const regions = ['All', 'Europe', 'Asia Pacific', 'Middle East'];
  const activePartners = partners.filter((p) => p.status === 'active').length;

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="GlobeAltIcon" size={20} className="text-primary" />
            <span className="text-sm font-medium text-primary">Global Airline Network</span>
          </div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Trusted by Leading Airlines Worldwide
          </h2>
          <p className="text-lg text-muted-foreground mb-6">
            Our platform connects with {activePartners}+ major airlines across continents, providing real-time PNR management and seamless booking experiences.
          </p>

          {/* Stats */}
          <div className="flex items-center justify-center space-x-8 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-success" />
              <span className="text-muted-foreground">{activePartners} Active</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-warning" />
              <span className="text-muted-foreground">2 Integrating</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-muted" />
              <span className="text-muted-foreground">2 Coming Soon</span>
            </div>
          </div>
        </div>

        {/* Region Filter */}
        <div className="flex items-center justify-center space-x-2 mb-12">
          {regions.map((region) =>
          <button
            key={region}
            className="px-4 py-2 rounded-lg text-sm font-medium text-foreground hover:bg-surface transition-colors duration-200">

              {region}
            </button>
          )}
        </div>

        {/* Partners Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {partners.map((partner, index) =>
          <div
            key={index}
            className="bg-card rounded-xl shadow-brand hover:shadow-brand-lg transition-all duration-300 overflow-hidden group">

              {/* Logo Container */}
              <div className="relative h-32 bg-surface overflow-hidden">
                <AppImage
                src={partner.logo}
                alt={partner.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute inset-0 bg-gradient-to-t from-card/80 to-transparent" />
                
                {/* Status Badge */}
                <div className="absolute top-3 right-3">
                  {getStatusBadge(partner.status)}
                </div>
              </div>

              {/* Partner Info */}
              <div className="p-4">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                  {partner.name}
                </h3>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <Icon name="MapPinIcon" size={14} />
                    <span>{partner.region}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="CalendarIcon" size={14} />
                    <span>{partner.integrationDate}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-8 lg:p-12">
          <Icon name="BuildingOfficeIcon" size={48} className="text-primary mx-auto mb-4" />
          <h3 className="font-heading text-2xl lg:text-3xl font-bold text-foreground mb-4">
            Become a Partner Airline
          </h3>
          <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
            Join our growing network of airline partners and unlock new revenue streams through our advanced booking platform and API integration.
          </p>
          <button className="inline-flex items-center space-x-2 bg-primary hover:bg-secondary text-white px-8 py-4 rounded-lg font-heading font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300">
            <span>Schedule Partnership Call</span>
            <Icon name="ArrowRightIcon" size={20} />
          </button>
        </div>
      </div>
    </section>);

}